import axios, { CancelTokenSource } from 'axios';
import uuidv4 from 'uuid';

import { Messages } from '../../codegen/Localize';
import { callViaApiService, UploadWorkbookErrorCodes, WorkbookUploadResponse } from '../../ts/restApi/WorkbookUpload';
import { RestApiErrorResponse } from '../../ts/services/RestApiService';
import { UploadWorkbookStartAction } from '../actions/UploadWorkbookActions';

function uploadWorkbookRequest(action: UploadWorkbookStartAction, projectLuid: string, progressDispatcher: (progress: any) => void, cancelToken: CancelTokenSource): Promise<WorkbookUploadResponse> {
  const { file, name, description, showSheetsAsTabs } = action.data;

  const boundaryVal = generateBoundaryVal();
  const requestHeader = getWorkbookPublishRequestWithHeader(name, description, projectLuid, showSheetsAsTabs);
  const requestBodyBlob = createRequestBodyBlob(boundaryVal, file, requestHeader);
  const queryParams = getQueryParam(action.overwrite);
  const headers = { 'Content-Type': 'multipart/mixed; boundary=' + boundaryVal };

  // TODO: Once @drbr's changes for PromiseLike go in, this "as Promise<...>" can be removed
  return callViaApiService(requestBodyBlob, headers, queryParams, progressDispatcher, cancelToken) as Promise<WorkbookUploadResponse>;
}

function createRequestBodyBlob(boundaryVal: string, file: File, requestHeader: string) {
  const boundaryStr = `--${boundaryVal}\r\n`;
  const contentHeader = getWorkbookContentHeader(file.name, file.size);
  const contentFooter = getFooter(boundaryVal);
  const contents = boundaryStr + requestHeader + boundaryStr + contentHeader;
  return new Blob([contents, file, contentFooter]);
}

function generateBoundaryVal() {
  return uuidv4();
}

function getQueryParam(overwrite: boolean) {
  return {
    'overwrite': overwrite,
    'skipConnectionCheck': true // Necessary to publish workbooks with live connections
  };
}

function getFooter(boundaryVal: string) {
  return `\r\n--${boundaryVal}--\r\n`;
}

function getWorkbookContentHeader(fileName: string, fileSize: number) {
  let contents = `Content-Disposition: name="tableau_workbook"; filename="${fileName}"\r\n`;
  contents += 'Content-Type: application/octet-stream\r\n';
  contents += `Content-Length: ${fileSize}\r\n\r\n`;
  return contents;
}

function getWorkbookPublishRequestWithHeader(name: string, description: string, projectid: string, showTabs: boolean) {
  const data = {
    'workbook': {
      'name': name,
      'description': description,
      'showTabs': showTabs,
      'project': { 'id': projectid }
    },
  };

  const dataStr = JSON.stringify(data);

  let payloadStr = 'Content-Disposition: name="request_payload"\r\n';
  payloadStr += 'Content-Type: application/json\r\n';
  payloadStr += `Content-Length: ${dataStr.length}\r\n\r\n`;
  payloadStr += dataStr + '\r\n';

  return payloadStr;
}

function createCancelToken(): CancelTokenSource {
  const CancelToken = axios.CancelToken;
  return CancelToken.source();
}

function getErrorMessage(response: RestApiErrorResponse): string {
  if (response?.data?.error?.detail?.length > 0) {
    return response.data.error.detail;
  }

  return Messages.workbookUploadFailureMessage();
}

function getActionMessage(response: RestApiErrorResponse): string {
  if (response?.data?.error?.code === String(UploadWorkbookErrorCodes.PUBLISHING_UNAUTHORIZED)) {
    return Messages.goBack();
  }

  return Messages.tryAgain();
}

export default {
  uploadWorkbookRequest,
  createCancelToken,
  getErrorMessage,
  getActionMessage
};

// For unit testing purposes
export const TestUploadWorkbookRequestHelper = {
  uploadWorkbookRequest,
  createRequestBodyBlob,
  getWorkbookPublishRequestWithHeader,
  getWorkbookContentHeader,
  getQueryParam,
  getFooter,
  generateBoundaryVal,
  createCancelToken
};
